
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import Company from '@/types/company'
import User from '@/types/user'
import axios from 'axios'
import Issue, { SolverEntry, SolverStatus } from '@/types/issue'
import { ServiceStates } from '@/types/serviceItem'
import draggable from 'vuedraggable'

interface CompanySelectRef {
  focus?: () => void
  $refs?: {
    vcSelect?: {
      setOpenState?: (open: boolean, opts?: { needFocus?: boolean }) => void
    }
  }
}

@Component({
  components: { draggable }
})
export default class MultiSolverSelector extends Vue {
  @Prop({ required: true }) settings!: Record<string, unknown>
  SolverStatus = SolverStatus
  isAdding = false
  newSolver = { companyId: null as number|null, userId: 0 }
  companiesList = [] as Company[]
  solversList = [] as User[]
  solversByCompany = {} as Record<number, User[]>
  loadingSolvers = false
  companiesLoading = false
  companiesLoaded = false

  mounted (): void {
    this.getCompanies()
    this.refreshSolverStatusesForEntries()
  }

  @Watch('isAdding')
  onIsAddingChange (visible: boolean): void {
    if (visible) {
      this.focusCompanySelect()
    }
  }

  @Watch('isWorkflowLocked')
  onWorkflowLockedChange (locked: boolean): void {
    if (locked && this.isAdding) {
      this.isAdding = false
      this.resetForm()
    }
  }

  @Watch('solverEntries', { deep: true })
  onSolverEntriesChange (): void {
    this.refreshSolverStatusesForEntries()
  }

  getCompanies () : void {
    if (this.companiesLoaded || this.companiesLoading) return
    this.companiesLoading = true
    axios.get(`${process.env.VUE_APP_API_URL}/companies/all`).then(res => {
      this.companiesList = res.data.data
      this.companiesLoaded = true
    }).finally(() => {
      this.companiesLoading = false
    })
  }

  onCompanyChange (): void {
    this.newSolver.userId = 0
    this.solversList = []
    if (!this.newSolver.companyId) return
    const company = this.companies.find(c => c.id === this.newSolver.companyId)
    if (!company || !company.active) return

    this.loadingSolvers = true
    this.fetchSolversForCompany(this.newSolver.companyId)
      .then(res => {
        this.solversList = res
        const firstActiveSolver = this.solversList.find(s => s.active)
        this.newSolver.userId = firstActiveSolver ? firstActiveSolver.id : 0
      })
      .finally(() => {
        this.loadingSolvers = false
      })
  }

  get companies (): Company[] {
    return this.companiesList.sort((a, b) => a.name.localeCompare(b.name))
  }

  get solverEntries (): SolverEntry[] {
    return (this.$store.getters['issueStore/SolverEntries'] as SolverEntry[]) || []
  }

  get draggableSolvers (): SolverEntry[] {
    return this.solverEntries
  }

  set draggableSolvers (value: SolverEntry[]) {
    this.$store.dispatch('issueStore/reorderWorkflowSolvers', value)
  }

  get currentItem (): Issue {
    return this.$store.getters['issueStore/current']
  }

  get isWorkflowLocked (): boolean {
    return this.currentItem.status === ServiceStates.Afgewezen
  }

  get hasRunningStep (): boolean {
    return this.solverEntries.some(s => [SolverStatus.ACTIVE, SolverStatus.READY].includes(s.status))
  }

  get canSaveNewSolver (): boolean {
    if (!this.newSolver.companyId || !this.newSolver.userId) return false
    const company = this.companies.find(c => c.id === this.newSolver.companyId)
    const user = this.solversList.find(u => u.id === this.newSolver.userId)

    return !!(company && company.active && user && user.active)
  }

  startWorkflow (): void {
    if (this.isWorkflowLocked) return
    this.$store.dispatch('issueStore/startWorkflow')
  }

  onDragChange (): void {
    console.log('New compact order saved:', this.solverEntries)
  }

  onMove (evt: { relatedContext?: { element?: SolverEntry } }): boolean {
    if (this.isWorkflowLocked) return false
    return !!(evt.relatedContext && evt.relatedContext.element && evt.relatedContext.element.status === SolverStatus.PENDING)
  }

  isDraggable (solver: SolverEntry): boolean {
    return !this.isWorkflowLocked && solver.status === SolverStatus.PENDING
  }

  addSolver (): void {
    if (this.isWorkflowLocked) return
    if (!this.canSaveNewSolver) return
    const company = this.companies.find(c => c.id === this.newSolver.companyId)
    const user = this.solversList.find(u => u.id === this.newSolver.userId)

    if (company && user && company.active && user.active) {
      this.$store.dispatch('issueStore/addWorkflowSolver', {
        company_id: company.id,
        company_name: company.name,
        company_active: company.active,
        user_id: user.id,
        user_name: user.full_name,
        user_active: user.active,
        status: SolverStatus.PENDING
      })
      this.isAdding = false
      this.resetForm()
    }
  }

  cancelAdd (): void {
    this.isAdding = false
    this.resetForm()
  }

  openAddForm (): void {
    if (this.isWorkflowLocked) return
    this.isAdding = true
  }

  resetForm (): void {
    this.newSolver.companyId = null
    this.newSolver.userId = 0
    this.solversList = []
  }

  removeSolver (index: number): void {
    if (this.isWorkflowLocked) return
    this.$store.dispatch('issueStore/removeWorkflowSolver', index)
  }

  onStepReady (solver: SolverEntry): void {
    if (this.isWorkflowLocked) return
    this.$store.dispatch('issueStore/readyWorkflowStep', solver)
  }

  onStepComplete (solver: SolverEntry): void {
    if (this.isWorkflowLocked) return
    this.$store.dispatch('issueStore/nextWorkflowStep', solver)
  }

  isFirstPending (solver: SolverEntry): boolean {
    const solvers = this.solverEntries
    const firstPendingIdx = solvers.findIndex(s => s.status === SolverStatus.PENDING)
    return solvers.indexOf(solver) === firstPendingIdx
  }

  canStartPendingStep (solver: SolverEntry): boolean {
    return !this.isWorkflowLocked && solver.status === SolverStatus.PENDING && !this.hasRunningStep && this.isFirstPending(solver)
  }

  onStepSetActive (solver: SolverEntry): void {
    if (this.isWorkflowLocked) return
    this.$store.dispatch('issueStore/rollbackWorkflowStep', solver)
  }

  refreshSolverStatusesForEntries (): void {
    const companyIds = Array.from(new Set(this.solverEntries.map(entry => entry.company_id).filter(Boolean)))
    companyIds.forEach(companyId => {
      if (!this.solversByCompany[companyId]) {
        this.fetchSolversForCompany(companyId)
      }
    })
  }

  fetchSolversForCompany (companyId: number): Promise<User[]> {
    return axios.get(`${process.env.VUE_APP_API_URL}/companies/${companyId}/solvers`)
      .then(res => {
        const solvers = res.data.data
        this.$set(this.solversByCompany, companyId, solvers)
        return solvers
      })
  }

  isCompanyInactive (entry: SolverEntry): boolean {
    const company = this.companiesList.find(c => c.id === entry.company_id)
    if (company) return !company.active
    if (typeof entry.company_active === 'boolean') return !entry.company_active
    return false
  }

  isSolverInactive (entry: SolverEntry): boolean {
    const solvers = this.solversByCompany[entry.company_id]
    const solver = solvers ? solvers.find(s => s.id === entry.user_id) : null
    if (solver) return !solver.active
    if (typeof entry.user_active === 'boolean') return !entry.user_active
    return false
  }

  hasInactiveSolver (entry: SolverEntry): boolean {
    return this.isCompanyInactive(entry) || this.isSolverInactive(entry)
  }

  focusCompanySelect (): void {
    this.$nextTick(() => {
      const companySelect = this.$refs.companySelect as CompanySelectRef | null
      if (companySelect && typeof companySelect.focus === 'function') {
        companySelect.focus()
      }
      const vcSelect = companySelect && companySelect.$refs ? companySelect.$refs.vcSelect : null
      if (vcSelect && typeof vcSelect.setOpenState === 'function') {
        vcSelect.setOpenState(true, { needFocus: true })
      }
    })
  }

  filterOptions (input: string, option: { componentOptions?: { children?: Array<{ text: string }> } }): boolean {
    const text = option.componentOptions && option.componentOptions.children && option.componentOptions.children[0]
      ? option.componentOptions.children[0].text
      : ''
    return String(text).toLowerCase().indexOf(input.toLowerCase()) >= 0
  }
}
